import React, { useState, useEffect } from 'react';
import { planService } from '../services/planService';
import OperatorLogo from './OperatorLogo';
import { Plus, Power, Trash2, RefreshCw } from 'lucide-react';
import PopupModal from './PopupModal';

export default function PlanManagement() {
  const [plans, setPlans] = useState([]);
  const [activeOperator, setActiveOperator] = useState('Jio');
  const [popup, setPopup] = useState(null);
  const [newPlan, setNewPlan] = useState({
    plan_id: '',
    operator: 'Jio',
    plan_type: '',
    name: '',
    price: '',
    validity_days: '28',
    description: '',
  });

  useEffect(() => {
    loadPlans();
  }, []);

  async function loadPlans() {
    try {
      const data = await planService.getAllPlansAdmin();
      setPlans(data || []);
    } catch (err) {
      console.error(err);
    }
  }

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newPlan.plan_type || !newPlan.plan_id.trim() || !newPlan.name.trim() || !newPlan.price) {
      setPopup({ title: "Incomplete Form", message: "Please fill all required plan fields." });
      return;
    }
    try {
      await planService.createPlan({
        ...newPlan,
        price: parseFloat(newPlan.price),
        validity_days: parseInt(newPlan.validity_days, 10),
        is_active: true,
      });
      setNewPlan({ plan_id: '', operator: activeOperator, plan_type: newPlan.plan_type, name: '', price: '', validity_days: '28', description: '' });
      await loadPlans();
      setPopup({ title: "Success", message: "Plan added successfully to catalog!" });
    } catch (err) {
      setPopup({ title: "Failed", message: err.message });
    }
  };

  const handleToggle = async (id, status) => {
    try {
      await planService.togglePlanStatus(id, status);
      await loadPlans();
    } catch (err) {
      setPopup({ title: "Error", message: err.message });
    }
  };

  const handleDelete = async (id) => {
    try {
      await planService.deletePlan(id);
      await loadPlans();
      setPopup({ title: "Deleted", message: "Plan removed from database." });
    } catch (err) {
      setPopup({ title: "Error", message: err.message });
    }
  };

  const currentPlans = plans.filter(p =>
    String(p.operator || '').trim().toLowerCase() === activeOperator.toLowerCase() &&
    String(p.plan_type || '').trim().toLowerCase() === newPlan.plan_type
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {["Jio", "Airtel", "Vi", "BSNL"].map(op => (
            <button
              key={op}
              onClick={() => { setActiveOperator(op); setNewPlan(prev => ({ ...prev, operator: op })); }}
              className={`px-5 py-2 rounded-full text-xs font-bold transition-all ${
                activeOperator === op ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-slate-700 border border-slate-200'
              }`}
            >
              {op} Plans
            </button>
          ))}
        </div>
        <button onClick={loadPlans} className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Add Plan Form */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Plus size={16} className="text-orange-500" /> Add New {activeOperator} {newPlan.plan_type} Plan
        </h3>
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <select
            required
            value={newPlan.plan_type}
            onChange={e => setNewPlan({ ...newPlan, plan_type: e.target.value })}
            className="p-3 border rounded-xl bg-white font-semibold capitalize"
          >
            <option value="" disabled>Select plan type</option>
            <option value="prepaid">Prepaid</option>
            <option value="postpaid">Postpaid</option>
          </select>
          <input
            type="text"
            required
            placeholder="Plan ID (e.g. jio-399)"
            value={newPlan.plan_id}
            onChange={e => setNewPlan({ ...newPlan, plan_id: e.target.value })}
            className="p-3 border rounded-xl"
          />
          <input
            type="text"
            required
            placeholder="Plan Name (e.g. 2GB/Day 5G)"
            value={newPlan.name}
            onChange={e => setNewPlan({ ...newPlan, name: e.target.value })}
            className="p-3 border rounded-xl"
          />
          <input
            type="number"
            required
            placeholder="Price (₹)"
            value={newPlan.price}
            onChange={e => setNewPlan({ ...newPlan, price: e.target.value })}
            className="p-3 border rounded-xl"
          />
          <input
            type="number"
            required
            placeholder="Validity (Days)"
            value={newPlan.validity_days}
            onChange={e => setNewPlan({ ...newPlan, validity_days: e.target.value })}
            className="p-3 border rounded-xl"
          />
          <textarea
            rows="3"
            placeholder="Description / Benefits"
            value={newPlan.description}
            onChange={e => setNewPlan({ ...newPlan, description: e.target.value })}
            className="p-3 border rounded-xl sm:col-span-2"
          />
          <button
            type="submit"
            className="sm:col-span-3 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl text-sm shadow-md"
          >
            + Create Plan
          </button>
        </form>
      </div>

      {/* Plans List Table */}
      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold border-b border-slate-200">
            <tr>
              <th className="p-4">Operator</th>
              <th className="p-4">Plan Name</th>
              <th className="p-4">Price</th>
              <th className="p-4">Validity</th>
              <th className="p-4">Status</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {currentPlans.map(p => (
              <tr key={p.id}>
                <td className="p-4"><OperatorLogo op={p.operator} size="sm" /></td>
                <td className="p-4 font-bold text-slate-900">{p.name}</td>
                <td className="p-4 font-extrabold text-emerald-600">₹{p.price}</td>
                <td className="p-4 text-slate-500 text-xs">{p.validity_days} Days</td>
                <td className="p-4">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${p.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                    {p.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="p-4 flex items-center gap-2">
                  <button onClick={() => handleToggle(p.id, p.is_active)} className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700">
                    <Power size={14} />
                  </button>
                  <button onClick={() => handleDelete(p.id)} className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-600">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PopupModal
        isOpen={!!popup}
        title={popup?.title || ""}
        message={popup?.message || ""}
        onClose={() => setPopup(null)}
      />
    </div>
  );
}